import {MNullable} from '../../Toolkit/Utils';
import * as Yup from 'yup';
import {AnySchema} from 'yup';
import NSwagAdapter from '../../Toolkit/Api/NSwagAdapter';
import {RequestClient} from '../RequestClients';

export class Request {

    public static readonly EstimateMaxLength = 30;
    public static readonly AdditionalRefMaxLength = 255;
    public static readonly RequestorMaxLength = 50;
    public static readonly ExchangeMaxLength = 50;
    public static readonly ProjectCodeMaxLength = 30;
    public static readonly WorkDescriptionMaxLength = 500;
    public static readonly Address1MaxLength = 50;
    public static readonly Address2MaxLength = 50;
    public static readonly Address3MaxLength = 50;
    public static readonly CityMaxLength = 50;
    public static readonly CountyMaxLength = 50;
    public static readonly PostcodeMaxLength = 10;
    public static readonly GridReferenceMaxLength = 300;
    public static readonly SlipNumberMaxLength = 30;
    public static readonly CivilsNameMaxLength = 100;
    public static readonly CivilsEmailMaxLength = 255;
    public static readonly CivilsPhoneMaxLength = 20;
    public static readonly InvoiceNumberMaxLength = 20;
    public static readonly JobDetailMaxLength = 255;
    public static readonly AddRecipientsMaxLength = 400;
    public static readonly PermitRefMaxLength = 400;

    public id: string = '';
    public estimate: string = '';
    public additionalRef: string = '';
    public requestor: string = '';
    public exchange: string = '';
    public slaType: number = 0;
    public projectValue: number = 0;
    public quoteStatus: number = 0;
    public quoteTechnician?: string = undefined;
    public quoteSubName?: string = undefined;
    public quoteCompleteDate: MNullable<Date> = null;
    public costClaimable: number = 0;
    public quoteValue: number = 0;
    public jobPriority: number = -1;
    public jobType: number[] = [];
    public jobDetail: string = '';
    public jobStatus: number = 0;
    public workStream: number = 3;
    public workDescription: string = '';
    public estimatedDays: number = 0;
    public address1: string = '';
    public address2: string = '';
    public address3: string = '';
    public city: string = '';
    public county: string = '';
    public postcode: string = '';
    public northing: number = 0;
    public easting: number = 0;
    public gridReference: string = '';
    public dueDate: MNullable<Date> = null;
    public slipNumber: string = '';
    public jobStartDate: MNullable<Date> = null;
    public endType: number = 0;
    public jobEndDate: MNullable<Date> = null;
    public selectedDays: Date[] = [];
    public noParkingRequired: boolean = false;
    public weekendWorks: boolean = false;
    public busStop: number = -1;
    public dayOnly: boolean = false;
    public civilsName: string = '';
    public civilsEmail: string = '';
    public civilsPhone: string = '';
    public siteDocsComplete: boolean = false;
    public invoiceNumber: string = '';
    public parent: string = '';
    public createdOn: MNullable<Date> = null;
    public locked: boolean = false;
    public addRecipients: string = '';
    public permitRef: string = '';
    public permitStatus: number = -1;
    public drawingRef: string = '';
    public resources: string[] = [];
    public commercialStatus: number = 0;
    public externalInvoicesReceived: number = 0;
    public jobCompletedDate: MNullable<Date> = null;
    public commercialCompleteDate: MNullable<Date> = null;
    public dfeStatus: number = 0;
    public worksTotalCost: number = 0;
    public uplift: number = 0;
    public dfeValue: number = 0;
    public dfePartialReason: number = 0;
    public dfePartialInfo: string = '';
    public sentToDepotnet: boolean = false;
    public actualCost: number = 0;
    public drawingsBy: string[] = [];
};

export class CreateRequestValidator {
    public static estimateFunc(fileClient: NSwagAdapter<RequestClient>, requestId: string): AnySchema{
        return Yup.string().label('Estimate').max(Request.EstimateMaxLength).required().test(
            'testDuplicate',
            'Estimate must be unique',
            function testDuplicate(value): Promise<boolean> {
                if(value !== undefined && value!.length <= Request.EstimateMaxLength) {
                    return new Promise(async(resolve, reject) => {
                        const currentEstimate = value;
                        const result = await fileClient.callWithReply(RequestMultiReply,
                            (client) => client.getEstimateData(currentEstimate));
                        result.throwIfFailed();

                        if(result.reply!.requests.length === 0 || result.reply!.requests[0].id === requestId) {
                            //Estimate is not unique so cancel the update.
                            resolve(true);
                        } else {
                            resolve(false);
                        }
                    });
                }

                return new Promise(async(resolve, reject) => {
                    resolve(true);
                });
            }
        );
    }

    public static readonly requestor = Yup.string().label('Requestor').max(Request.RequestorMaxLength).required();
    public static readonly exchange = Yup.string().label('Exchange').min(1, 'Exchange is required').max(Request.ExchangeMaxLength).required();
    public static readonly additionalRef = Yup.string().label('AdditionalRef').max(Request.AdditionalRefMaxLength).required();
    public static readonly slaType = Yup.number().label('SlaType').min(0, 'SLA type is required').required();
    public static readonly projectValue = Yup.number().label('Project Value').min(0, 'Project code is required').required();
    public static readonly workStream = Yup.number().label('Workstream').min(0,'Work Type is required').required();
    public static readonly workDescription = Yup.string().label('WorkDescription').max(Request.WorkDescriptionMaxLength).optional();
    public static readonly estimatedDays = Yup.number().label('EstimatedDays').min(0).max(1000).required();
    public static readonly address1 = Yup.string().label('Address1').max(Request.Address1MaxLength).optional();
    public static readonly address2 = Yup.string().label('Address2').max(Request.Address2MaxLength).optional();
    public static readonly address3 = Yup.string().label('Address3').max(Request.Address3MaxLength).optional();
    public static readonly city = Yup.string().label('City').max(Request.CityMaxLength).optional();
    public static readonly county = Yup.string().label('County').max(Request.CountyMaxLength).optional();
    public static readonly postcode = Yup.string().label('Postcode').max(Request.PostcodeMaxLength).optional();
    public static readonly northing = Yup.number().label('Northing').test(
        'northingValue',
        'Value must be 6 digits',
        (value) => {
            if(value === undefined) {
                return true;
            }
            if(value >= 100000 && value <= 999999) {
                return true;
            }
            return false;
        }
    );
    public static readonly easting = Yup.number().label('Easting').test(
        'northingValue',
        'Value must be 6 digits',
        (value) => {
            if(value === undefined) {
                return true;
            }
            if(value >= 100000 && value <= 999999) {
                return true;
            }
            return false;
        }
    );
    public static readonly gridReference = Yup.string().label('Gridreference').max(Request.GridReferenceMaxLength).nullable();
    public static readonly addRecipient = Yup.string().nullable().test(
        'addRecipients',
        'Additional Recipients must contain a list of valid emails.',
        (value) => {
            try {
                if(value !== null && value !== undefined && value.length > Request.AddRecipientsMaxLength) {
                    return false;
                }
                if(value === null || value === undefined || value.trim().length === 0) {
                    return true;
                }
                return (value.trim()
                             .toLowerCase()
                             .match(/^([a-z0-9,!#$%&'*+/=?^_`{|}~-]+(\.[a-z0-9,!#$%&'*+/=?^_`{|}~-]+)*@[a-z0-9-]+(\.[a-z0-9-]+)*\.([a-z]{2,}))(\s*(;|,|\n)\s*[a-z0-9,!#$%&'*+/=?^_`{|}~-]+(\.[a-z0-9,!#$%&'*+/=?^_`{|}~-]+)*@[a-z0-9-]+(\.[a-z0-9-]+)*\.([a-z]{2,}))*$/g)?.length ?? 0) > 0;
            }
            catch {
                return false;
            }
        }
    );
    public static readonly dfePartialReason = Yup.number().test(
        'dfeReason',
        'Reason must be selected',
        (value, ctx) => {
            if(ctx.parent.dfeStatus === 7) {
                if(value === -1) {
                    return false;
                }
            }
            return true;
        }
    );
};

export class CreateJobValidator {
    public static readonly slipNumber = Yup.string().label('slipNumber').max(Request.SlipNumberMaxLength).required();
    public static readonly jobStartDate = Yup.date()
                                             .min(new Date(), 'Start date cannot be earlier than today')
                                             .label('jobStartDate')
                                             .required();
    public static readonly jobEndDate = Yup.date().nullable().test(
      'jobEndDate',
      'End date is required and must be later than start date',
        (value, ctx) => {
          if(ctx.parent.endType === 1) {
              return true;
          }
          if(value === null || value === undefined || value < ctx.parent.jobStartDate) {
              return false;
          }
          return true;
        }
    );
    public static readonly selectedDays = Yup.array().test(
        "selectedDays",
        "Day selection is required",
        (value, ctx) => {
            if(ctx.parent.endType === 1) {
                return true;
            }
            return (value?.length ?? 0) > 0;
        });
    public static readonly jobPriority = Yup.number().nullable().required().min(0, "Job Priority must be set");
    public static readonly civilsName = Yup.string().label('civilsName').max(Request.CivilsNameMaxLength).optional();
    public static readonly civilsEmail = Yup.string().email().label('civilsEmail').max(Request.CivilsEmailMaxLength).optional();
    public static readonly civilsPhone = Yup.string().label('civilsPhone').max(Request.CivilsPhoneMaxLength).optional();
    public static readonly busStop = Yup.number().typeError('Bus Stop Suspension type is required').label('busStop')
                                        .min(0,'Bus Stop Suspension type is required');
    public static readonly noParkingDate = Yup.date().nullable().test(
        'noParkingDate',
        'Date must be entered as No Parking Cones are required',
        (value, ctx) => {
            if(ctx.parent.request.noParkingRequired === true && (value === undefined || value === null)) {
                return false;
            }
            return true;
        }
    );
    public static readonly workStartTime = Yup.date().required('Start time is required');
    public static readonly workEndTime = Yup.date().nullable().test(
        'workEndTime',
        'End time is required',
        (value, ctx) => {
            if(ctx.parent.request.endType === 1) {
                return true;
            }
            if(value === null || value === undefined) {
                return false;
            }
            return true;
        });
}


export class UpdateRequestValidator {
    public static readonly exchange = Yup.string().label('Exchange').min(1, 'Exchange is required').max(Request.ExchangeMaxLength).required();
    public static readonly additionalRef = Yup.string().label('AdditionalRef').max(Request.AdditionalRefMaxLength).required();
    public static readonly slaType = Yup.number().label('SlaType').min(-1).max(255).required();
    public static readonly projectValue = Yup.number().label('Project Value').min(0, 'Project code is required').required();
    public static readonly workStream = Yup.number().label('Workstream').min(0,'Work Type is required').required();
    public static readonly workDescription = Yup.string().label('WorkDescription').max(Request.WorkDescriptionMaxLength).optional();
    public static readonly jobEndDate = Yup.date().nullable().test(
        'jobEndDate',
        'End date is required and must be later than start date',
        (value, ctx) => {
            if(ctx.parent.endType === 1) {
                return true;
            }
            if(value === null || value === undefined || value < ctx.parent.jobStartDate) {
                return false;
            }
            return true;
        }
    );
    public static readonly selectedDays = Yup.array().test(
        "selectedDays",
        "Day selection is required",
        (value, ctx) => {
            if(ctx.parent.endType === 1) {
                return true;
            }
            return (value?.length ?? 0) > 0;
        });
    public static readonly estimatedDays = Yup.number().label('EstimatedDays').min(0).max(1000).required();
    public static readonly address1 = Yup.string().label('Address1').max(Request.Address1MaxLength).optional();
    public static readonly address2 = Yup.string().label('Address2').max(Request.Address2MaxLength).optional();
    public static readonly address3 = Yup.string().label('Address3').max(Request.Address3MaxLength).optional();
    public static readonly city = Yup.string().label('City').max(Request.CityMaxLength).optional();
    public static readonly county = Yup.string().label('County').max(Request.CountyMaxLength).optional();
    public static readonly postcode = Yup.string().label('Postcode').max(Request.PostcodeMaxLength).optional();
    public static readonly northing = Yup.number().label('Northing').test(
        'northingValue',
        'Value must be 6 digits',
        (value) => {
            if(value === undefined) {
                return true;
            }
            if(value >= 100000 && value <= 999999) {
                return true;
            }
            return false;
        }
    );
    public static readonly easting = Yup.number().label('Easting').test(
        'northingValue',
        'Value must be 6 digits or 0',
        (value) => {
            if(value === undefined) {
                return true;
            }
            if(value >= 100000 && value <= 999999) {
                return true;
            }
            return false;
        }
    );
    public static readonly gridReference = Yup.string().label('Gridreference').max(Request.GridReferenceMaxLength).nullable();
    public static readonly jobType = Yup.array().nullable().test(
        'jobType',
        'Job Type must contain a value',
        (value, ctx) => {
            if(ctx.parent.quoteStatus !== 3 || (Array.isArray(value) && value.length > 0)) {
                return true;
            }
            return false;
        }
    );
    public static readonly jobDetail = Yup.string().label('Job Detail').max(Request.JobDetailMaxLength).optional();
    public static readonly costClaimable = Yup.number().label("Cost Claimable").nullable().test(
        'costClaimable',
        'Cost Claimable value is required before completion',
        (value, ctx) => {
            if(ctx.parent.quoteStatus !== 3 || (value !== null && (value ?? 0) > -1)) {
                return true;
            }
            return false;
        }
    )
    public static readonly quoteValue = Yup.number().nullable().test(
        'quoteValue',
        'Quote Value is required before completion',
        (value, ctx) => {
            if(ctx.parent.quoteStatus !== 3 || (value !== null && (value ?? 0) > -1)) {
                return true;
            }
            return false;
        }
    );
    public static readonly noParkingRequired = Yup.boolean().nullable().test(
        'noParkingRequired',
        'No Parking value is required to complete Quote',
        (value, ctx) => {
            if(ctx.parent.quoteStatus !== 3 || value !== null) {
                return true;
            }
            return false;
        }
    );
    public static readonly busStop = Yup.number().nullable().test(
        'busStop',
        'Bus Stop value required to complete quote',
        (value, ctx) => {
            if(ctx.parent.quoteStatus !== 3 || (value !== null && value! > -1)) {
                return true;
            }
            return false;
        }

    );
    public static readonly invoiceNumber = Yup.string().max(Request.InvoiceNumberMaxLength).optional();
    public static readonly addRecipient = Yup.string().nullable().test(
        'addRecipients',
        'Additional Recipients must contain a list of valid emails.',
        (value) => {
            try {
                if(value !== null && value !== undefined && value.length > Request.AddRecipientsMaxLength) {
                    return false;
                }
                if(value === null || value === undefined || value.trim().length === 0) {
                    return true;
                }
                return (value.trim()
                             .toLowerCase()
                             .match(/^([a-z0-9,!#$%&'*+/=?^_`{|}~-]+(\.[a-z0-9,!#$%&'*+/=?^_`{|}~-]+)*@[a-z0-9-]+(\.[a-z0-9-]+)*\.([a-z]{2,}))(\s*(;|,|\n)\s*[a-z0-9,!#$%&'*+/=?^_`{|}~-]+(\.[a-z0-9,!#$%&'*+/=?^_`{|}~-]+)*@[a-z0-9-]+(\.[a-z0-9-]+)*\.([a-z]{2,}))*$/g)?.length ?? 0) > 0;
            }
            catch {
                return false;
            }
        }
    );
    public static readonly permitRef = Yup.string().label('PermitRef').max(Request.PermitRefMaxLength).optional();
    public static readonly dfeStatus = Yup.number().test(
        'dfeStatus',
        'Reason & Information must be entered for Partial status.',
        (value, ctx) => {
            if(value === 7 && ((ctx.parent.dfePartialReason ?? -1) === -1
                || (ctx.parent.dfePartialInfo ?? '').length === 0)) {
                return false;
            }
            return true;
        }
    );
    public static readonly dfePartialReason = Yup.number().test(
        'dfeReason',
        'Reason must be selected',
        (value, ctx) => {
            if(ctx.parent.dfeStatus === 7 && (value === undefined || value === -1)) {
                return false;
            }
            return true;
        }
    );
    public static readonly dfePartialInfo = Yup.string().test(
        'dfeReason',
        'Reason must be selected',
        (value, ctx) => {
            if(ctx.parent.dfeStatus === 7 && (value === undefined || value.length === 0)) {
                return false;
            }
            return true;
        }
    );
    public static readonly workStartTime = Yup.date().required('Start time is required');
    public static readonly workEndTime = Yup.date().nullable().test(
        'workEndTime',
        'End time is required',
        (value, ctx) => {
            if(ctx.parent.request.endType === 1) {
                return true;
            }
            if(value === null || value === undefined || value.getFullYear() < 2000) {
                return false;
            }
            return true;
        });
}

export class RequestCountReply {
    public count: number = 0;
}

export class RequestMultiReply {
    public requests: Request[] = [];
};

export class RequestReply {
    public request: MNullable<Request> = null;
};
